import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, ShieldCheck, Check } from "lucide-react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";
import { db } from "../../firebase";

export default function PrivacySettings() {
  const { profile, profileId, loading, error, setError } = useSettingsProfile();
  const [profileVisibility, setProfileVisibility] = useState("everyone");
  const [hidePhotos, setHidePhotos] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const saveQueue = useRef(Promise.resolve());
  const lastSaved = useRef(null);
  const savedProfileVisibility = profile?.profileVisibility || "everyone";
  const savedHidePhotos = Boolean(profile?.hidePhotos);

  useEffect(() => {
    if (loading) return;
    lastSaved.current = {
      profileVisibility: savedProfileVisibility,
      hidePhotos: savedHidePhotos,
    };
    setProfileVisibility(savedProfileVisibility);
    setHidePhotos(savedHidePhotos);
    setInitialized(true);
  }, [loading, savedProfileVisibility, savedHidePhotos]);

  useEffect(() => {
    if (!initialized || loading || !profileId) return undefined;
    if (
      lastSaved.current?.profileVisibility === profileVisibility &&
      lastSaved.current?.hidePhotos === hidePhotos
    ) {
      return undefined;
    }

    setSaveStatus("saving");
    setError("");
    const timeoutId = window.setTimeout(() => {
      const update = {
        profileVisibility,
        hidePhotos,
        updatedAt: serverTimestamp(),
      };
      saveQueue.current = saveQueue.current
        .then(() => updateDoc(doc(db, "profiles", profileId), update))
        .then(() => {
          lastSaved.current = { profileVisibility, hidePhotos };
          setSaveStatus("saved");
        })
        .catch((err) => {
          setSaveStatus("");
          setError(err.message || "Could not save privacy settings.");
        });
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [initialized, loading, profileId, profileVisibility, hidePhotos, setError]);

  const visibilityOptions = [
    { value: "everyone", title: "Everyone", desc: "Visible in matches to all eligible users", Icon: Eye },
    { value: "premium", title: "Premium only", desc: "Only premium members can see your profile", Icon: ShieldCheck },
    { value: "hidden", title: "Hidden", desc: "Pause your profile and hide from matches", Icon: EyeOff },
  ];

  return (
    <SettingsLayout
      title="Privacy"
      description="Control who can discover your profile and whether your photos are shown."
    >
      {loading ? (
        <div className="card flex justify-center p-10">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : !profileId ? (
        <div className="card p-6">
          <p className="text-[14px] text-ink-500">You need a profile before privacy settings apply.</p>
          <Link to="/create-profile" className="btn-primary mt-4 px-5">Create profile</Link>
        </div>
      ) : (
        <>
          <section className="overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-sm">
            <div className="px-4 pb-3 pt-4 sm:px-5 sm:pt-5">
              <h2 className="text-[15px] font-semibold text-ink-800">Profile visibility</h2>
              <p className="mt-1 text-[12px] leading-5 text-ink-500">Choose who can discover your profile.</p>
            </div>
            <div className="px-4 pb-1 sm:px-5">
              {visibilityOptions.map(({ value, title, desc, Icon }) => {
                const active = profileVisibility === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setProfileVisibility(value)}
                    aria-pressed={active}
                    className={`flex w-full items-center gap-3 border-t border-sand-100 py-3.5 text-left transition hover:bg-sand-50/70 ${
                      active ? "text-brand-700" : "text-ink-700"
                    }`}
                  >
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                        active ? "bg-brand-50 text-brand-700" : "bg-sand-50 text-ink-500"
                      }`}
                    >
                      <Icon size={17} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold">{title}</p>
                      <p className="mt-0.5 text-[11.5px] leading-4 text-ink-500">{desc}</p>
                    </div>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                        active ? "border-brand-600 bg-brand-600 text-white" : "border-sand-300 bg-white"
                      }`}
                    >
                      {active && <Check size={12} strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <label className="mt-3 flex cursor-pointer items-center justify-between gap-4 rounded-3xl border border-sand-200 bg-white p-4 shadow-sm transition hover:border-sand-300 sm:p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sand-50 text-ink-500">
                <EyeOff size={17} />
              </span>
              <div>
                <p className="text-[13px] font-semibold text-ink-800">Hide photos</p>
                <p className="mt-0.5 text-[11.5px] leading-4 text-ink-500">
                  Others see a placeholder instead of your images.
                </p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={hidePhotos}
              onChange={(e) => setHidePhotos(e.target.checked)}
              className="h-5 w-5 shrink-0 accent-brand-600"
            />
          </label>

          {saveStatus && (
            <p
              role="status"
              className={`fixed left-1/2 top-[5.25rem] z-50 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-[12px] shadow-lg ${
                saveStatus === "saved"
                  ? "border-emerald-100 bg-white text-emerald-700"
                  : "border-sand-200 bg-white text-ink-600"
              }`}
            >
              {saveStatus === "saved" ? <Check size={14} /> : <Loader2 size={14} className="animate-spin" />}
              {saveStatus === "saved" ? "Changes saved automatically" : "Saving changes..."}
            </p>
          )}
          {error && (
            <p className="mt-3 rounded-2xl bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
          )}
        </>
      )}
    </SettingsLayout>
  );
}
